import JSZip from 'jszip';
import type { SignaturePlacement } from './pdfSign';
import { log } from './logger';
import { pngAspectRatio } from '../utils/pngSize';

/** A4 page size in EMUs (English Metric Units). 914400 EMU = 1 inch. */
const PAGE_WIDTH_EMU = 7560318; // 210 mm
const PAGE_HEIGHT_EMU = 10692550; // 297 mm

/**
 * Embeds a floating PNG signature into a DOCX (OOXML) via JSZip.
 * Position uses UI top-left fractions relative to an A4 page.
 */
export async function embedSignatureOnDocx(
  docxBytes: Uint8Array,
  pngBytes: Uint8Array,
  placement: SignaturePlacement,
): Promise<Uint8Array> {
  const zip = await JSZip.loadAsync(docxBytes);

  const mediaPath = pickMediaPath(zip);
  zip.file(mediaPath, pngBytes);

  const relsPath = 'word/_rels/document.xml.rels';
  const relsXml = await readText(zip, relsPath);
  if (!relsXml) {
    throw new Error('DOCX ist ungültig (document.xml.rels fehlt).');
  }
  const { relsOut, relId } = addImageRelationship(relsXml, mediaPath.replace(/^word\//, ''));
  zip.file(relsPath, relsOut);

  await ensurePngContentType(zip);

  const docPath = 'word/document.xml';
  const docXml = await readText(zip, docPath);
  if (!docXml) {
    throw new Error('DOCX ist ungültig (document.xml fehlt).');
  }
  const drawing = buildFloatingPngDrawing(relId, placement, pngBytes);
  zip.file(docPath, insertDrawingBeforeBodyEnd(docXml, drawing));

  const out = await zip.generateAsync({
    type: 'uint8array',
    compression: 'DEFLATE',
  });
  log('DOCX Signatur eingebettet', { relId, mediaPath, pageIndex: placement.pageIndex });
  return out;
}

function pickMediaPath(zip: JSZip): string {
  let n = 1;
  while (zip.file(`word/media/signature${n}.png`)) n += 1;
  return `word/media/signature${n}.png`;
}

async function readText(zip: JSZip, path: string): Promise<string | null> {
  const f = zip.file(path);
  if (!f) return null;
  return f.async('string');
}

function addImageRelationship(
  relsXml: string,
  targetRelativeToWord: string,
): { relsOut: string; relId: string } {
  const ids = [...relsXml.matchAll(/Id="(rId\d+)"/g)].map((m) =>
    Number(m[1].replace('rId', '')),
  );
  const next = (ids.length ? Math.max(...ids) : 0) + 1;
  const relId = `rId${next}`;
  const rel =
    `<Relationship Id="${relId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="${escapeXml(targetRelativeToWord)}"/>`;
  if (!relsXml.includes('</Relationships>')) {
    throw new Error('DOCX Relationships-Datei ist beschädigt.');
  }
  return {
    relId,
    relsOut: relsXml.replace('</Relationships>', `${rel}</Relationships>`),
  };
}

async function ensurePngContentType(zip: JSZip): Promise<void> {
  const path = '[Content_Types].xml';
  const xml = await readText(zip, path);
  if (!xml || /Extension="png"/i.test(xml)) return;
  if (!xml.includes('</Types>')) return;
  const override = `<Default Extension="png" ContentType="image/png"/>`;
  zip.file(path, xml.replace('</Types>', `${override}</Types>`));
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function buildFloatingPngDrawing(
  relId: string,
  placement: SignaturePlacement,
  pngBytes: Uint8Array,
): string {
  const aspect = pngAspectRatio(pngBytes, 2.5);
  // Width from UI; height MUST follow PNG aspect — never use heightFrac*pageHeight
  // (that made Word draw a tall box with empty space under the ink).
  const cx = Math.max(91440, Math.round(placement.widthFrac * PAGE_WIDTH_EMU));
  const cy = Math.max(91440, Math.round(cx / aspect));
  const ox = Math.max(0, Math.round(placement.leftFrac * PAGE_WIDTH_EMU));
  const oy = Math.max(0, Math.round(placement.topFrac * PAGE_HEIGHT_EMU));
  const docPrId = 1000 + Math.floor(Math.random() * 100000);

  return (
    `<w:p>` +
    `<w:r>` +
    `<w:drawing>` +
    `<wp:anchor distT="0" distB="0" distL="0" distR="0" simplePos="0" relativeHeight="251658240" behindDoc="0" locked="0" layoutInCell="1" allowOverlap="1">` +
    `<wp:simplePos x="0" y="0"/>` +
    `<wp:positionH relativeFrom="page"><wp:posOffset>${ox}</wp:posOffset></wp:positionH>` +
    `<wp:positionV relativeFrom="page"><wp:posOffset>${oy}</wp:posOffset></wp:positionV>` +
    `<wp:extent cx="${cx}" cy="${cy}"/>` +
    `<wp:effectExtent l="0" t="0" r="0" b="0"/>` +
    `<wp:wrapNone/>` +
    `<wp:docPr id="${docPrId}" name="Signatur"/>` +
    `<wp:cNvGraphicFramePr>` +
    `<a:graphicFrameLocks xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" noChangeAspect="1"/>` +
    `</wp:cNvGraphicFramePr>` +
    `<a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">` +
    `<a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">` +
    `<pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">` +
    `<pic:nvPicPr>` +
    `<pic:cNvPr id="0" name="signature.png"/>` +
    `<pic:cNvPicPr><a:picLocks noChangeAspect="1"/></pic:cNvPicPr>` +
    `</pic:nvPicPr>` +
    `<pic:blipFill><a:blip r:embed="${relId}"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill>` +
    `<pic:spPr>` +
    `<a:xfrm><a:off x="0" y="0"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm>` +
    `<a:prstGeom prst="rect"><a:avLst/></a:prstGeom>` +
    `</pic:spPr>` +
    `</pic:pic>` +
    `</a:graphicData>` +
    `</a:graphic>` +
    `</wp:anchor>` +
    `</w:drawing>` +
    `</w:r>` +
    `</w:p>`
  );
}

function insertDrawingBeforeBodyEnd(docXml: string, drawingParagraph: string): string {
  let xml = docXml;
  if (!xml.includes('xmlns:wp=')) {
    xml = xml.replace(
      /<w:document([^>]*)>/,
      '<w:document$1 xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    );
  }
  if (!xml.includes('</w:body>')) {
    throw new Error('DOCX document.xml ohne w:body.');
  }
  return xml.replace('</w:body>', `${drawingParagraph}</w:body>`);
}
